import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Settings2, Loader2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function SettingsModal({ open, onOpenChange, project, onUpdated }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState(project);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(project); }, [project]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, {
        title: form.title, subtitle: form.subtitle, author_name: form.author_name,
        language: form.language, genre: form.genre, description: form.description,
      });
      onUpdated(data);
      toast.success(t("toast.saved"));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.delete(`/projects/${project.id}`);
    navigate("/dashboard");
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><Settings2 className="w-5 h-5 text-gold" /> {t("studio.settings")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5 mt-1">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.title")}</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-ink-900 border-white/10 focus-visible:ring-gold" data-testid="settings-title" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.subtitle")}</Label>
            <Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.author")}</Label>
              <Input value={form.author_name || ""} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.language")}</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="bg-ink-900 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.genre")}</Label>
            <Input value={form.genre || ""} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.description")}</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-ink-900 border-white/10 focus-visible:ring-gold resize-none" />
          </div>
          <div className="flex items-center justify-between pt-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="settings-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1.5" /> {t("modal.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-ink-800 border-white/10 text-slate-100">
                <AlertDialogHeader><AlertDialogTitle className="font-serif">{t("modal.deleteConfirm")}</AlertDialogTitle></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-ink-900 border-white/10 text-slate-300">{t("modal.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-500 hover:bg-red-600 text-white" data-testid="settings-delete-confirm">{t("modal.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button type="submit" disabled={saving} className="bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="settings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("modal.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
